'use client';

import React, { useState } from 'react';
import { colors, baseStyles } from './SharedUI';

// ==========================================
// COMPONENT CHÍNH
// ==========================================
// [ĐÃ BỔ SUNG]: Prop onScanComplete để truyền dữ liệu sang LiquidationSqueezeRadar
export default function SupportResistanceRadar({ symbol, liveData, histDataList, onScanComplete }) {
  const [loading, setLoading] = useState(false);
  
  // State Cấu hình Tier (Bộ lọc nhiễu) - Mặc định chuyển sang 0.05 cho Tier 1 mới
  const [selectedTier, setSelectedTier] = useState("0.05"); 

  // State Dữ liệu Vùng Cản
  const [supports, setSupports] = useState([]);
  const [resistances, setResistances] = useState([]);
  
  // State Báo cáo Đối chiếu
  const [distanceReport, setDistanceReport] = useState([]);
  const [lastScan, setLastScan] = useState(null);

  const formatPrice = (price) => {
    if (!price) return '--';
    if (price >= 1000) return `${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `${price.toFixed(4)}`;
    return `${price.toFixed(6)}`;
  };

  // Hàm gộp các mốc giá nằm quá sát nhau (Lọc nhiễu theo Tier)
  const mergeZones = (zones, thresholdPct) => {
    let merged = [];
    let sorted = [...zones].sort((a, b) => a - b);
    sorted.forEach(price => {
      const isDuplicate = merged.find(m => (Math.abs(m - price) / price) <= thresholdPct);
      if (!isDuplicate) merged.push(price);
    });
    return merged;
  };

  // ==========================================
  // THUẬT TOÁN QUÉT VÀ XUẤT BÁO CÁO (ĐÃ VÁ LỖI DYNAMIC ZONES)
  // ==========================================
  const executeRadar = async () => {
    const currentPrice = liveData?.last;
    if (!symbol || !currentPrice) {
      alert("Chưa có dữ liệu Giá hiện tại để làm mốc tham chiếu!");
      return;
    }

    if (!histDataList || histDataList.length === 0) {
      alert("Vui lòng Fetch dữ liệu Futures (Lịch sử) ở bảng trên trước khi quét cản!");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=4h&limit=100`);
      const klines = await res.json();

      let rawResistances = [];
      let rawSupports = [];

      for (let i = 2; i < klines.length - 2; i++) {
        const prev2H = parseFloat(klines[i - 2][2]), prev1H = parseFloat(klines[i - 1][2]);
        const currH = parseFloat(klines[i][2]);
        const next1H = parseFloat(klines[i + 1][2]), next2H = parseFloat(klines[i + 2][2]);

        const prev2L = parseFloat(klines[i - 2][3]), prev1L = parseFloat(klines[i - 1][3]);
        const currL = parseFloat(klines[i][3]);
        const next1L = parseFloat(klines[i + 1][3]), next2L = parseFloat(klines[i + 2][3]);

        if (currH > prev1H && currH > prev2H && currH > next1H && currH > next2H) rawResistances.push(currH);
        if (currL < prev1L && currL < prev2L && currL < next1L && currL < next2L) rawSupports.push(currL);
      }

      const threshold = parseFloat(selectedTier);
      const cleanResistances = mergeZones(rawResistances, threshold);
      const cleanSupports = mergeZones(rawSupports, threshold);

      // [QUAN TRỌNG]: Gộp chung tất cả các mốc Cản vào một "Rổ Tổng"
      // Vì Hỗ trợ của hôm nay có thể là Kháng cự của hôm qua và ngược lại
      const allCleanZones = [...cleanResistances, ...cleanSupports];

      // Phân rổ hiển thị cho mục TOP 3 (Dựa trên giá LIVE)
      const finalRes = allCleanZones.filter(p => p > currentPrice).sort((a, b) => a - b).slice(0, 3);
      const finalSup = allCleanZones.filter(p => p < currentPrice).sort((a, b) => b - a).slice(0, 3);

      setResistances(finalRes);
      setSupports(finalSup);

      let reportItems = [];

      // Phân tích các mốc Lịch sử (Tự tìm Cản Động cho từng thời điểm)
      histDataList.forEach(hist => {
        const pClose = hist.close;
        
        // [VÁ LỖI]: Tìm Kháng cự/Hỗ trợ gần nhất so với giá của CHÍNH KHUNG GIỜ NÀY
        const dynamicRes = allCleanZones.filter(p => p > pClose).sort((a, b) => a - b)[0] || null;
        const dynamicSup = allCleanZones.filter(p => p < pClose).sort((a, b) => b - a)[0] || null;

        const distR = dynamicRes ? ((dynamicRes - pClose) / pClose) * 100 : null;
        const distS = dynamicSup ? ((pClose - dynamicSup) / pClose) * 100 : null;

        let highlightColor = colors.textSub;
        let highlightBg = 'rgba(255,255,255,0.03)';
        let statusText = "CÂN BẰNG (Lơ lửng ở giữa)";

        // [CẬP NHẬT LOGIC MỚI]: Báo cáo dựa trên giá Close (distR và distS)
        if (distR !== null && distS !== null) {
          if (distR < distS) {
            highlightColor = colors.green; highlightBg = 'rgba(34, 197, 94, 0.05)'; 
            statusText = "🟢 GẦN KHÁNG CỰ (Đang có đà bơm lên trần)";
          } else if (distS < distR) {
            highlightColor = colors.red; highlightBg = 'rgba(239, 68, 68, 0.05)'; 
            statusText = "🔴 GẦN HỖ TRỢ (Bị đè sát xuống sàn)";
          }
        }

        // ==========================================
        // Tính màu và ghi chú tham chiếu cho GIÁ LIVE
        // ==========================================
        const distR_toLive = dynamicRes ? Math.abs(dynamicRes - currentPrice) : null;
        const distS_toLive = dynamicSup ? Math.abs(currentPrice - dynamicSup) : null;
        
        let liveColorInfo = colors.textMain;
        let liveNote = "";

        if (distR_toLive !== null && distS_toLive !== null) {
          if (distR_toLive < distS_toLive) {
            liveColorInfo = colors.red; 
            liveNote = "(🔴 Tiến gần Kháng cự)";
          } else {
            liveColorInfo = colors.green; 
            liveNote = "(🟢 Tiến gần Hỗ trợ)";
          }
        } else if (distR_toLive !== null) {
          liveColorInfo = colors.red; 
          liveNote = "(🔴 Tiến gần Kháng cự)";
        } else if (distS_toLive !== null) {
          liveColorInfo = colors.green; 
          liveNote = "(🟢 Tiến gần Hỗ trợ)";
        }

        // TÍNH PHẦN TRĂM LỆCH GIỮA GIÁ CLOSE VÀ GIÁ LIVE
        const liveDiffPct = ((currentPrice - pClose) / pClose) * 100;

        reportItems.push({
          id: hist.id,
          label: hist.label.toUpperCase(),
          price: pClose,
          distRes: distR,
          distSup: distS,
          valRes: dynamicRes, 
          valSup: dynamicSup, 
          color: highlightColor,
          bg: highlightBg,
          statusText,
          // Truyền thuộc tính Giá Live vào
          livePrice: currentPrice,
          liveColorInfo,
          liveNote,
          liveDiffPct // Lưu % lệch để in ra UI
        });
      });

      // Phân tích mốc Live Price (Hiện tại)
      const liveRes = finalRes[0] || null;
      const liveSup = finalSup[0] || null;

      const distR_Live = liveRes ? ((liveRes - currentPrice) / currentPrice) * 100 : null;
      const distS_Live = liveSup ? ((currentPrice - liveSup) / currentPrice) * 100 : null;
      
      let liveColor = colors.textSub;
      let liveBg = 'rgba(255,255,255,0.03)';
      let liveStatusText = "CÂN BẰNG (Lơ lửng ở giữa)";

      if (distR_Live !== null && distS_Live !== null) {
        if (distR_Live < distS_Live) {
          liveColor = colors.green; liveBg = 'rgba(34, 197, 94, 0.1)';
          liveStatusText = "🟢 GẦN KHÁNG CỰ (Phe Mua đang hưng phấn)";
        } else if (distS_Live < distR_Live) {
          liveColor = colors.red; liveBg = 'rgba(239, 68, 68, 0.1)';
          liveStatusText = "🔴 GẦN HỖ TRỢ (Phe Bán đang hoảng loạn ép giá)";
        }
      }

      // ==========================================
      // Tính màu và ghi chú tham chiếu cho GIÁ LIVE (Bản thân item Live)
      // ==========================================
      const distR_toLiveLive = liveRes ? Math.abs(liveRes - currentPrice) : null;
      const distS_toLiveLive = liveSup ? Math.abs(currentPrice - liveSup) : null;
      
      let liveItemColorInfo = colors.textMain;
      let liveItemNote = "";

      if (distR_toLiveLive !== null && distS_toLiveLive !== null) {
        if (distR_toLiveLive < distS_toLiveLive) {
          liveItemColorInfo = colors.red; 
          liveItemNote = "(🔴 Tiến gần Kháng cự)";
        } else {
          liveItemColorInfo = colors.green; 
          liveItemNote = "(🟢 Tiến gần Hỗ trợ)";
        }
      } else if (distR_toLiveLive !== null) {
        liveItemColorInfo = colors.red; 
        liveItemNote = "(🔴 Tiến gần Kháng cự)";
      } else if (distS_toLiveLive !== null) {
        liveItemColorInfo = colors.green; 
        liveItemNote = "(🟢 Tiến gần Hỗ trợ)";
      }

      reportItems.push({
        id: 'live',
        label: 'HIỆN TẠI (LIVE)',
        price: currentPrice,
        distRes: distR_Live,
        distSup: distS_Live,
        valRes: liveRes,
        valSup: liveSup,
        color: liveColor,
        bg: liveBg,
        statusText: liveStatusText,
        // Truyền thuộc tính Giá Live vào
        livePrice: currentPrice,
        liveColorInfo: liveItemColorInfo,
        liveNote: liveItemNote,
        liveDiffPct: 0
      });

      // [ĐÃ BỔ SUNG LẠI]: Truyền dữ liệu lên component cha để thằng LiquidationSqueezeRadar dùng
      if (onScanComplete) {
        onScanComplete({ res: liveRes, sup: liveSup });
      }

      setDistanceReport(reportItems);
      setLastScan(new Date().toLocaleTimeString('vi-VN'));

    } catch (error) {
      console.error("Lỗi khi quét S/R:", error);
      alert("Có lỗi xảy ra khi gọi dữ liệu Binance!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...baseStyles.card, marginTop: '24px' }}>
      
      {/* ================= HEADER CONTROL ================= */}
      <div style={{ ...baseStyles.flexBetween, borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <strong style={{ color: colors.textMain, fontSize: '15px' }}>🔭 RADAR DÒ CẢN & KHOẢNG CÁCH</strong>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: colors.textSub }}>ĐỘ NHẠY GỘP VÙNG:</span>
            <select 
              value={selectedTier} 
              onChange={(e) => setSelectedTier(e.target.value)} 
              style={{ backgroundColor: colors.bg, color: 'white', border: `1px solid ${colors.border}`, borderRadius: '4px', padding: '4px', fontSize: '12px', cursor: 'pointer' }}
            >
              {/* [CẬP NHẬT]: Các thông số Tier mới */}
              <option value="0.05">Tier 1: Xe Lu (0.5% - 5%)</option>
              <option value="0.12">Tier 2: Altcoin (5.5% - 12%)</option>
              <option value="0.25">Tier 3: Meme/Low-cap (12.5% - 25%)</option>
            </select>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {lastScan && <span style={{ fontSize: '11px', color: colors.textSub }}>Cập nhật: {lastScan}</span>}
          <button 
            onClick={executeRadar} 
            disabled={loading}
            style={{ 
              backgroundColor: colors.bg, border: `1px solid ${colors.warn}`, color: colors.warn, 
              padding: '6px 16px', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', 
              fontWeight: 'bold', fontSize: '12px'
            }}
          >
            {loading ? '⏳ ĐANG QUÉT...' : '⚡ EXECUTE DÒ CẢN'}
          </button>
        </div>
      </div>

      {/* ================= HIỂN THỊ KẾT QUẢ GỐC ================= */}
      {!lastScan && !loading && (
        <div style={{ textAlign: 'center', color: colors.textSub, padding: '20px', fontSize: '13px' }}>
          Vui lòng chọn Tier phù hợp với Coin và bấm <b>EXECUTE</b> để xuất báo cáo khoảng cách.
        </div>
      )}

      {lastScan && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* 1. MỐC CẢN TÌM ĐƯỢC */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <div style={{ backgroundColor: colors.redBg, border: `1px solid rgba(239, 68, 68, 0.3)`, borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontWeight: 'bold', color: colors.red, marginBottom: '12px' }}>🔴 TOP 3 KHÁNG CỰ</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {resistances.length > 0 ? resistances.map((res, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: colors.bg, padding: '8px', borderRadius: '4px', borderLeft: `3px solid ${colors.red}` }}>
                    <span style={{ color: colors.textMain, fontWeight: 'bold', fontSize: '13px' }}>{formatPrice(res)}</span>
                  </div>
                )) : <span style={{fontSize: '12px', color: colors.textSub}}>Không có cản.</span>}
              </div>
            </div>

            <div style={{ backgroundColor: colors.greenBg, border: `1px solid rgba(34, 197, 94, 0.3)`, borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontWeight: 'bold', color: colors.green, marginBottom: '12px' }}>🟢 TOP 3 HỖ TRỢ</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {supports.length > 0 ? supports.map((sup, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', backgroundColor: colors.bg, padding: '8px', borderRadius: '4px', borderLeft: `3px solid ${colors.green}` }}>
                    <span style={{ color: colors.textMain, fontWeight: 'bold', fontSize: '13px' }}>{formatPrice(sup)}</span>
                  </div>
                )) : <span style={{fontSize: '12px', color: colors.textSub}}>Không có cản.</span>}
              </div>
            </div>
          </div>

          {/* 2. BÁO CÁO KHOẢNG CÁCH (TIMELINE) */}
          <div style={{ marginTop: '8px', borderTop: `1px dashed ${colors.border}`, paddingTop: '16px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: colors.textMain, marginBottom: '12px' }}>
              📊 BÁO CÁO ĐỘNG LƯỢNG GIÁ KẾT HỢP DÒNG THỜI GIAN
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {distanceReport.map((rep) => (
                <div key={rep.id} style={{ 
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', 
                  padding: '12px 16px', backgroundColor: rep.bg, borderRadius: '6px', 
                  borderLeft: `4px solid ${rep.color}` 
                }}>
                  
                  {/* Cột 1: Tên Khung giờ */}
                  <div style={{ minWidth: '130px', fontWeight: 'bold', fontSize: '13px', color: colors.textMain }}>
                    [ {rep.label} ]
                  </div>
                  
                  {/* Cột 2: Chi tiết Khoảng cách */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ fontWeight: 'bold', color: rep.color, fontSize: '13px' }}>
                      {rep.statusText}
                    </div>
                    
                    <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                      {/* [CẬP NHẬT UI]: Chuyển % sang dòng Giá Close */}
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        Giá Close/Live:&nbsp;<strong style={{color: colors.textMain}}>${formatPrice(rep.price)}</strong>
                        {rep.id !== 'live' && rep.liveDiffPct !== undefined && (
                          <span style={{ marginLeft: '6px', color: rep.liveDiffPct >= 0 ? colors.green : colors.red, fontWeight: 'bold' }}>
                            ({rep.liveDiffPct >= 0 ? '+' : ''}{rep.liveDiffPct.toFixed(2)}%)
                          </span>
                        )}
                      </span>
                      
                      {/* Hiển thị giá live kết hợp màu và ghi chú sát bên cạnh giá mốc */}
                      <span>
                        Giá Live: <strong style={{color: rep.liveColorInfo}}>${formatPrice(rep.livePrice)} {rep.liveNote}</strong>
                      </span>
                      
                      {/* Hiển thị rõ giá của Hỗ trợ đi kèm % */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Cách Hỗ Trợ <span style={{ color: colors.red, fontWeight: 'bold' }}>{rep.valSup ? `[${formatPrice(rep.valSup)}]` : '[N/A]'}</span> : {' '}
                        <strong style={{color: rep.distSup !== null && rep.distSup < (rep.distRes || 999) ? colors.red : colors.textMain}}>
                          {rep.distSup !== null ? `-${rep.distSup.toFixed(2)}%` : 'N/A'}
                        </strong>
                      </span>
                      
                      {/* Hiển thị rõ giá của Kháng cự đi kèm % */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Cách Kháng Cự <span style={{ color: colors.green, fontWeight: 'bold' }}>{rep.valRes ? `[${formatPrice(rep.valRes)}]` : '[N/A]'}</span> : {' '}
                        <strong style={{color: rep.distRes !== null && rep.distRes < (rep.distSup || 999) ? colors.green : colors.textMain}}>
                          {rep.distRes !== null ? `+${rep.distRes.toFixed(2)}%` : 'N/A'}
                        </strong>
                      </span>
                    </div>
                  </div>

                </div>
              ))}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
